import React, { useEffect, useState, useRef, useContext } from 'react';
import { Dropdown, Button, Popover, Overlay } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import moment from 'moment';
import _ from 'lodash';

import { Number } from '../../common/common';
import DatePickerCustomized from '../../add_transaction/stepone/date_picker_customized/date_picker_customized.component';
import './custom_ranged_gain.component.css';

import AppContext from '../../../context';

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const CustomRangedGain = (props) => {
  const startDateMap = useRef({
    '1D': 1,
    '1W': 7,
    '1M': 30,
    '3M': 90,
    '6M': 180,
    '1YR': 365,
    '3YR': 1095,
    '5YR': 1825,
  });
  const datepickerTarget = useRef(null);
  const fromCalendar = useRef(null);
  const toCalendar = useRef(null);
  const context = useContext(AppContext);
  //state
  const [ranges, setRanges] = useState([
    ['1D', true],
    ['1W', true],
    ['1M', true],
    ['3M', true],
    ['6M', true],
    ['1YR', true],
    ['3YR', true],
    ['5YR', true],
    ['CUSTOM', true],
  ]);
  const [gain, setGain] = useState(props.defaultValue);
  const [range, setRange] = useState(ranges[0]);
  const prevRange = usePrevious(range);
  const [dateRange, setDateRange] = useState([]);
  const prevDateRange = usePrevious(dateRange);
  const [minStartDate, setMinStartDate] = useState(null);
  const [minEndDate, setMinEndDate] = useState(null);
  const [isDatepickerVisible, setIsDatepickerVisible] = useState(false);
  //resp
  const [activeCalendar, setActiveCalendar] = useState('');

  const getDisplayDate = (d) => {
    return moment(d).format('DD-MM-YY');
  };

  const getPayloadDate = (d) => {
    return moment(d).format('YYYY-MM-DD');
  };

  const getTitle = () => {
    return range === 'CUSTOM' ? (
      <div>
        <FontAwesomeIcon icon={faCalendar} className="mr-1" />
        {getDisplayDate(dateRange[0])}
        <span className="mx-2">-</span>
        <FontAwesomeIcon icon={faCalendar} className="mr-1" />
        {getDisplayDate(dateRange[1])}
      </div>
    ) : (
      range
    );
  };

  const datePickerPopover = () => (
    <Popover className="date-range">
      <Popover.Content>
        <div className="dr-nav">
          <button
            type="button"
            className={`${activeCalendar === 'from' ? 'active' : ''} from`}
            onClick={() => setActiveCalendar('from')}
          >
            FROM
          </button>
          <button
            type="button"
            className={`${activeCalendar === 'to' ? 'active' : ''} to`}
            onClick={() => setActiveCalendar('to')}
          >
            TO
          </button>
        </div>
        <div className="br-right dr-from" ref={fromCalendar}>
          <div className="dr-title">FROM</div>
          <DatePickerCustomized
            inline={true}
            minDate={minStartDate}
            selected={dateRange[0]}
            onSelect={(d) => {
              const _dateRange = [...dateRange];
              _dateRange[0] = d;
              setDateRange(_dateRange);
            }}
            calendarClassName="date-range-picker left"
          />
        </div>
        <div className="dr-to" ref={toCalendar}>
          <div className="dr-title">TO</div>
          <DatePickerCustomized
            inline={true}
            minDate={minEndDate}
            selected={dateRange[1]}
            onSelect={(d) => {
              const _dateRange = [...dateRange];
              _dateRange[1] = d;
              setDateRange(_dateRange);
            }}
            calendarClassName="date-range-picker right"
          />
        </div>
      </Popover.Content>
    </Popover>
  );

  const get_data_class = (data) => {
    let color_class = '';
    if (data !== 0) {
      color_class = data < 0 ? 'c-negative negative' : 'c-positive positive';
    }
    return color_class + ' data';
  };

  useEffect(() => {
    const endDate = new Date();
    setDateRange([
      startDateMap.current[range === 'CUSTOM' ? prevRange : range],
      endDate,
    ]);
  }, [range]);

  useEffect(() => {
    //sets current start date as minimum end date
    setMinEndDate(dateRange[0]);
    const _fetchGain = async (startDate, endDate) => {
      try {
        const res = await props.api(props.portfolioId, startDate, endDate);
        setGain(Math.floor(res.data.gains));
      } catch (error) {
        setRange(prevRange);
      }
    };
    if (dateRange.length === 2) {
      let [startDate, endDate] = dateRange;
      startDate = getPayloadDate(startDate);
      endDate = getPayloadDate(endDate);
      if (prevDateRange) {
        const [prevStartDate, prevEndDate] = prevDateRange;
        if (
          startDate === getPayloadDate(prevStartDate) &&
          endDate === getPayloadDate(prevEndDate)
        ) {
          return;
        }
      }
      if (range === '1D') {
        setGain(props.defaultValue);
        return;
      }
      _fetchGain(startDate, endDate);
    }
  }, [dateRange]);

  useEffect(() => {
    setMinStartDate(props.minStartDate);
    const minStartDate = new Date(props.minStartDate);
    const _ranges = _.cloneDeep(ranges);
    Object.entries(startDateMap.current).forEach(([key, value], idx) => {
      let startDate = new Date();
      startDate.setDate(startDate.getDate() - value);
      if (startDate > minStartDate) {
        startDateMap.current[key] = startDate;
      } else {
        startDateMap.current[key] = minStartDate;
        _ranges[idx][1] = false;
      }
    });
    setRanges(_ranges);
    //resp
    const handleResize = () => {
      if (window.innerWidth < 500) {
        setActiveCalendar('from');
      } else {
        setActiveCalendar('');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  //resp
  useEffect(() => {
    if (!fromCalendar.current || !toCalendar.current) return;
    if (!activeCalendar) {
      fromCalendar.current.style.display = 'block';
      toCalendar.current.style.display = 'block';
      return;
    }
    if (activeCalendar === 'from') {
      fromCalendar.current.style.display = 'block';
      toCalendar.current.style.display = 'none';
      return;
    }
    if (activeCalendar === 'to') {
      toCalendar.current.style.display = 'block';
      fromCalendar.current.style.display = 'none';
      return;
    }
  }, [activeCalendar]);

  return (
    <>
      <div className="head">
        Gain
        <Dropdown>
          <Button
            variant="secondary"
            className="dropdown-btn"
            ref={datepickerTarget}
            onClick={() => {
              if (range === 'CUSTOM') setIsDatepickerVisible(true);
            }}
          >
            {getTitle()}
          </Button>
          <Dropdown.Toggle split id="dropdown-split-basic" />
          <Dropdown.Menu
            className="vertical-scroll"
            popperConfig={{ container: datepickerTarget.current }}
          >
            {ranges.map((_range, idx) => (
              <Dropdown.Item
                key={_range[0]}
                eventKey={_range[0]}
                onClick={() => setRange(_range[0])}
                disabled={!_range[1]}
              >
                {_range[0]}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <Overlay
          show={isDatepickerVisible}
          target={datepickerTarget.current}
          placement="bottom-start"
          onHide={() => setIsDatepickerVisible(false)}
          rootClose
        >
          {datePickerPopover()}
        </Overlay>
      </div>
      <div className={get_data_class(gain)}>
        <Number value={gain} usePrefix={true} format={true} isFormatWeb={true} />
      </div>
    </>
  );
};

export default CustomRangedGain;
